/**
 * OverviewBento · «Обзор» dashboard bento (HANDOFF_board_overview.md · P2).
 *
 * Jira-style overview: 9 widgets, every number computed from real `Progress`
 * state (Genie: «вся статистика реально считается»). The heavy pure derivations
 * live in `lib/overviewData.ts` (unit-tested); course-level derivations
 * (currentSprintOrd · streak · skills · nearest milestone) are computed once by
 * the parent Progress screen and passed in to avoid double work.
 *
 * Design ref: `04_DESIGN/mockup_overview_dashboard.html` (riso bento · ink axes,
 * stamp bars). Widgets that migrated OFF the board rail per Genie 3 JUL live here.
 */

import { useMemo } from 'react';
import type { Progress } from '@shared/schemas';
import { SPRINTS } from '@shared/data/sprints';
import { allTickets } from '@shared/data/tickets';
import { BADGES_BY_ID } from '@shared/engine/badge-catalog';
import type { DerivedSkills } from '@/lib/derivedSkills';
import {
  aggregateIstqbByChapter,
  deriveBlockStamps,
  deriveDailyClosures,
  deriveWeekJournal,
  derive7DayRhythm,
  weekdayLabelRu,
} from '@/lib/overviewData';

export interface NearestMilestone {
  title: string;
  afterLabel: string;
  eta?: string;
  ticketsLeft: number;
}

interface OverviewBentoProps {
  progress: Progress;
  currentSprintOrd: number;
  streakDays: number;
  skills: DerivedSkills;
  nearestMilestone?: NearestMilestone;
  onSprint: (sprintId?: string) => void;
  /** Injectable clock for deterministic tests · defaults to Date.now(). */
  nowMs?: number;
}

const WEEKDAY_SHORT_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export function OverviewBento({
  progress,
  currentSprintOrd,
  streakDays,
  skills,
  nearestMilestone,
  onSprint,
  nowMs,
}: OverviewBentoProps) {
  const now = nowMs ?? Date.now();

  /* --- Спринт сейчас --- */
  const sprintNow = useMemo(() => {
    const sprint = SPRINTS[currentSprintOrd - 1];
    if (!sprint) return null;
    const estById = new Map(allTickets().map((t) => [t.id, t.estimateMin]));
    const ids = sprint.ticketIds;
    let done = 0;
    let remainMin = 0;
    for (const id of ids) {
      const st = progress.tickets[id]?.status;
      if (st === 'done') done += 1;
      else remainMin += estById.get(id) ?? 0;
    }
    const total = ids.length;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);
    const nextId = ids.find((id) => progress.tickets[id]?.status !== 'done');
    return { sprint, done, total, pct, remainMin, nextId };
  }, [progress.tickets, currentSprintOrd]);

  /* --- Курс · печати блоков --- */
  const stamps = useMemo(() => deriveBlockStamps(progress.cockpit), [progress.cockpit]);
  const stampsDone = stamps.filter((s) => s.status === 'done').length;

  /* --- Ритм 7 дней --- */
  const rhythm = useMemo(
    () => derive7DayRhythm(progress.achievements.pomodoroSessions, now),
    [progress.achievements.pomodoroSessions, now],
  );

  /* --- Закрытия за 14 дней --- */
  const closures = useMemo(() => deriveDailyClosures(progress, now, 14), [progress, now]);

  /* --- ISTQB по главам --- */
  const istqb = useMemo(() => aggregateIstqbByChapter(progress.cockpit), [progress.cockpit]);

  /* --- Журнал недели --- */
  const journal = useMemo(() => deriveWeekJournal(progress.events, now, 6), [progress.events, now]);

  /* --- Умения топ-5 --- */
  const topSkills = useMemo(() => {
    const rows = [
      ...skills.inProgress.map((s) => ({ id: s.id, label: s.label, pct: s.pct })),
      ...skills.earned.map((s) => ({ id: s.id, label: s.label, pct: 100 })),
    ];
    // Highest first; earned (100%) naturally lead — truthful «топ».
    rows.sort((a, b) => b.pct - a.pct || a.label.localeCompare(b.label));
    return rows.slice(0, 5);
  }, [skills]);

  /* --- Достижения (последние 3) --- */
  const recentBadges = useMemo(() => {
    return [...progress.achievements.earned]
      .sort((a, b) => b.earnedAt - a.earnedAt)
      .slice(0, 3)
      .map((e) => {
        const def = BADGES_BY_ID[e.badgeId];
        return { id: e.badgeId, name: def?.name ?? e.badgeId, hint: def?.description ?? '' };
      });
  }, [progress.achievements.earned]);

  const chartW = 560;
  const chartH = 150;
  const axisX = 30;
  const baseY = 120;
  const topY = 12;
  const barW = 24;
  const gap = (chartW - axisX - 15 - closures.bars.length * barW) / closures.bars.length;

  return (
    <div className="bento" data-testid="overview-bento">
      {/* Спринт сейчас */}
      <section className="ov-w ov-c4" aria-label="Спринт сейчас">
        <b className="ov-wt">Спринт сейчас</b>
        {sprintNow ? (
          <>
            <div className="ov-spr-num">
              SPRINT {String(sprintNow.sprint.ord).padStart(2, '0')} ·{' '}
              {sprintNow.sprint.category.toUpperCase()}
            </div>
            <div className="ov-spr-name">{sprintNow.sprint.title}</div>
            <div className="ov-spr-bar">
              <i style={{ width: `${sprintNow.pct}%` }} />
            </div>
            <div className="ov-spr-meta">
              {sprintNow.done} из {sprintNow.total} тикетов
              {sprintNow.remainMin > 0
                ? ` · осталось ~${Math.round(sprintNow.remainMin / 60)} ч`
                : ' · всё закрыто'}
            </div>
            <button type="button" className="ov-go" onClick={() => onSprint(sprintNow.sprint.id)}>
              {sprintNow.nextId ? `Продолжить с ${sprintNow.nextId}` : 'Открыть спринт'}
            </button>
          </>
        ) : (
          <div className="ov-empty">Спринт не выбран.</div>
        )}
      </section>

      {/* Курс · печати блоков */}
      <section className="ov-w ov-c4" aria-label="Печати блоков">
        <b className="ov-wt">Курс · печати блоков</b>
        <div className="ov-stamps">
          {stamps.map((s) => (
            <div key={s.n} className={`ov-stamp ${s.status}`} data-block={s.n}>
              <b>{s.label}</b>
              {s.status === 'done' ? `${Math.round(s.pct ?? 0)}%` : '—'}
            </div>
          ))}
          <span className="ov-stamp-lbl">
            {stampsDone > 0 ? `Пройдено вех: ${stampsDone}/3` : 'Первая веха — после S05'}
          </span>
        </div>
      </section>

      {/* Ритм 7 дней */}
      <section className="ov-w ov-c4" aria-label="Ритм 7 дней">
        <b className="ov-wt">Ритм · 7 дней</b>
        <div className="ov-streak-row">
          {rhythm.days.map((d) => (
            <span
              key={d.dayKey}
              className={`ov-day ${d.active ? 'on' : ''} ${d.isToday ? 'today' : ''}`}
              title={d.dayKey}
            />
          ))}
        </div>
        <div className="ov-streak-note">
          стрик <b>{streakDays}</b> {plural(streakDays, 'день', 'дня', 'дней')} ·{' '}
          {rhythm.weekPomodoros} {plural(rhythm.weekPomodoros, 'помидор', 'помидора', 'помидоров')}{' '}
          за неделю
          {rhythm.bestWeekday !== null ? ` · лучший день — ${weekdayLabelRu(rhythm.bestWeekday)}` : ''}
        </div>
      </section>

      {/* Закрытия за 14 дней */}
      <section className="ov-w ov-c7" aria-label="Закрытия за 14 дней">
        <b className="ov-wt">Закрытия за 14 дней</b>
        {closures.total === 0 ? (
          <div className="ov-empty">Закрой первый тикет — здесь появится динамика.</div>
        ) : (
          <>
            <svg viewBox={`0 0 ${chartW} ${chartH}`} width="100%" role="img" aria-label="Закрытые тикеты и домашки по дням">
              <line x1={axisX} y1={baseY} x2={chartW - 15} y2={baseY} stroke="var(--ink)" strokeWidth="2" />
              <line x1={axisX} y1={topY} x2={axisX} y2={baseY} stroke="var(--ink)" strokeWidth="2" />
              {closures.bars.map((bar, i) => {
                const h = Math.round((bar.count / closures.max) * (baseY - topY));
                const x = axisX + 8 + i * (barW + gap);
                return (
                  <rect
                    key={bar.dayKey}
                    x={x}
                    y={baseY - h}
                    width={barW}
                    height={h}
                    fill={bar.isToday ? 'var(--accent)' : 'var(--teal-soft)'}
                    stroke={h > 0 ? 'var(--ink)' : 'none'}
                    strokeWidth="1.5"
                  />
                );
              })}
              <text x={axisX} y={140} fontSize="10" fill="var(--ink-mute)">
                14 дней назад
              </text>
              <text x={chartW - 70} y={140} fontSize="10" fill="var(--ink-mute)">
                сегодня
              </text>
              <text x="8" y={topY + 6} fontSize="10" fill="var(--ink-mute)">
                {closures.max}
              </text>
              <text x="8" y={baseY - 2} fontSize="10" fill="var(--ink-mute)">
                0
              </text>
            </svg>
            <div className="ov-chart-note">
              тикеты + домашки · всего за 2 недели — {closures.total}
            </div>
          </>
        )}
      </section>

      {/* ISTQB по главам */}
      <section className="ov-w ov-c5" aria-label="ISTQB готовность по главам">
        <b className="ov-wt">ISTQB · готовность по главам</b>
        {!istqb.anyData ? (
          <div className="ov-empty">Появится после первого мок-экзамена или экзамена-вехи.</div>
        ) : (
          <>
            {istqb.chapters.map((c) => (
              <div className="ov-ch" key={c.chapter}>
                <span className="ov-ch-nm">{c.label}</span>
                <span className="ov-ch-tr">
                  <i style={{ width: `${c.pct}%` }} />
                </span>
                <span className="ov-ch-pc">{c.hasData ? `${c.pct}%` : '—'}</span>
              </div>
            ))}
            {istqb.weakest && (
              <div className="ov-chart-note">
                по мокам и экзаменам-вехам · слабое место — {istqb.weakest.label}
              </div>
            )}
          </>
        )}
      </section>

      {/* Журнал недели */}
      <section className="ov-w ov-c5" aria-label="Журнал недели">
        <b className="ov-wt">Журнал недели</b>
        {journal.length === 0 ? (
          <div className="ov-empty">Журнал заполнится по мере работы за последние 7 дней.</div>
        ) : (
          journal.map((r) => (
            <div className="ov-jrow" key={r.id}>
              <span className="ov-jd">{formatJournalTime(r.at, now)}</span>
              <span className="ov-jtext">{r.text}</span>
              <span className={`ov-jtag ${r.tag}`}>{JOURNAL_TAG_LABEL[r.tag]}</span>
            </div>
          ))
        )}
      </section>

      {/* Умения топ-5 */}
      <section className="ov-w ov-c4" aria-label="Умения топ-5">
        <b className="ov-wt">Умения · топ-5</b>
        {topSkills.length === 0 ? (
          <div className="ov-empty">Закрывай тикеты — умения начнут расти.</div>
        ) : (
          <>
            {topSkills.map((s) => (
              <div className="ov-sk" key={s.id}>
                <div className="ov-sk-nm">
                  <span>{s.label}</span>
                  <span>{s.pct}%</span>
                </div>
                <div className="ov-sk-tr">
                  <i style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
            <div className="ov-chart-note">все умения — в развороте ниже</div>
          </>
        )}
      </section>

      {/* Достижения */}
      <section className="ov-w ov-c3" aria-label="Достижения">
        <b className="ov-wt">Достижения</b>
        {recentBadges.length === 0 ? (
          <div className="ov-empty">Первый бейдж — за первый закрытый спринт.</div>
        ) : (
          <div className="ov-ach">
            {recentBadges.map((b) => (
              <span className="ov-badge" key={b.id} title={b.hint}>
                {b.name}
                {b.hint ? <small>{b.hint}</small> : null}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Ближайшая веха */}
      <section className="ov-w ov-c5" aria-label="Ближайшая веха">
        <b className="ov-wt">Ближайшая веха</b>
        {nearestMilestone ? (
          <div className="ov-mile">
            <div className="ov-seal">{nearestMilestone.afterLabel.replace('Sprint ', 'S')}</div>
            <p>
              <b>{nearestMilestone.title}</b> откроется после {nearestMilestone.afterLabel}
              {nearestMilestone.ticketsLeft > 0
                ? ` — осталось ${nearestMilestone.ticketsLeft} ${plural(
                    nearestMilestone.ticketsLeft,
                    'тикет',
                    'тикета',
                    'тикетов',
                  )}`
                : ''}
              {nearestMilestone.eta ? `. По ритму — ${nearestMilestone.eta}.` : '.'}
            </p>
          </div>
        ) : (
          <div className="ov-empty">Все контрольные точки трека пройдены 🎓</div>
        )}
      </section>
    </div>
  );
}

const JOURNAL_TAG_LABEL: Record<'close' | 'exam' | 'start' | 'badge', string> = {
  close: 'закрыт',
  exam: 'экзамен',
  start: 'старт',
  badge: 'бейдж',
};

/** Russian plural picker (one / few / many). */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** Relative-day + time label «сегодня · 14:45» / «вчера · 19:02» / «пн · 20:11». */
function formatJournalTime(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const startOf = (ms: number) => {
    const x = new Date(ms);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const daysAgo = Math.round((startOf(nowMs) - startOf(t)) / 86_400_000);
  if (daysAgo <= 0) return `сегодня · ${time}`;
  if (daysAgo === 1) return `вчера · ${time}`;
  return `${WEEKDAY_SHORT_RU[d.getDay()]} · ${time}`;
}
